"use client"

import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
    Box,
    Input,
    Button,
    FormControl,
    FormLabel,
    VStack,
    Text,
    Container,
    Center,
    Heading,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Alert,
    AlertIcon,
    ScaleFade,
    useColorModeValue,
    Divider,
    Icon,
    FormHelperText,
    useToast,
    HStack,
    PinInput,
    PinInputField,
    Progress,
    Flex,
} from "@chakra-ui/react"
import { EmailIcon, LockIcon, ViewIcon, ViewOffIcon, } from "@chakra-ui/icons"
import { confirmPasswordResetAsync } from "../Slice/PasswordReset/PasswordResetSlice"
import bg from "../image/image1.png";

const ResetPasswordForm = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const toast = useToast()
    const { loading, error } = useSelector((state) => state.password)

    const email = location.state?.email || "" // Read email from previous page

    const [resetCode, setResetCode] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    // Theme colors
    const bgColor = useColorModeValue("white", "gray.800")
    const cardBgGradient = useColorModeValue("linear(to-br, white, gray.50)", "linear(to-br, gray.800, gray.900)")
    const primaryColor = useColorModeValue("blue.500", "blue.300")
    const headingColor = useColorModeValue("blue.600", "blue.300")
    const textColor = useColorModeValue("gray.600", "gray.300")
    const iconBgColor = useColorModeValue("blue.50", "blue.900")
    const cardBg = useColorModeValue("white", "gray.800")

    // Password strength calculation
    const calculatePasswordStrength = (password) => {
        if (!password) return 0

        let strength = 0
        if (password.length >= 8) strength += 25
        if (password.match(/[A-Z]/)) strength += 25
        if (password.match(/[0-9]/)) strength += 25
        if (password.match(/[^A-Za-z0-9]/)) strength += 25

        return strength
    }

    const passwordStrength = calculatePasswordStrength(newPassword)

    const getPasswordStrengthColor = () => {
        if (passwordStrength <= 25) return "red"
        if (passwordStrength <= 50) return "orange"
        if (passwordStrength <= 75) return "yellow"
        return "green"
    }

    const getPasswordStrengthText = () => {
        if (passwordStrength <= 25) return "Weak"
        if (passwordStrength <= 50) return "Fair"
        if (passwordStrength <= 75) return "Good"
        return "Strong"
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (newPassword !== confirmPassword) {
            toast({
                title: "Passwords don't match",
                description: "Please ensure both passwords are the same.",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top",
            })
            return
        }

        dispatch(
            confirmPasswordResetAsync({
                email,
                code: resetCode,
                password: newPassword,
                password_confirmation: confirmPassword,
            }),
        ).then((res) => {
            if (res.meta.requestStatus === "fulfilled") {
                toast({
                    title: "Password Reset Successful",
                    description: "Your password has been updated. Redirecting to login...",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                    position: "top",
                })
                navigate("/login")
            }
        })
    }

    return (
        <div className="min-h-screen maindiv flex items-center justify-center px-4 py-20 font-p">
            <div className="flex bg-white shadow-2xl rounded-lg overflow-hidden max-w-6xl min-h-[60vh]  w-full">
                <div className="w-full md:w-1/2 p-8">
                    <h1 className="text-left font-bold text-2xl text-textBlue uppercase">
                        INDIA Hirings
                    </h1>
                    <p className="mt-2 text-left text-gray-600">
                        Reset Your Password
                    </p>

                    <Box
                        bg={cardBg}
                    >
                        <VStack spacing={1} align="center" >
                            {/* Lock Icon */}

                            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                                <VStack spacing={3} width="full">
                                    <FormControl isReadOnly>
                                        <FormLabel fontWeight="medium">Email</FormLabel>
                                        <InputGroup size="md">
                                            <InputLeftElement pointerEvents="none" children={<EmailIcon color="gray.400" />} />
                                            <Input
                                                type="email"
                                                value={email}
                                                isReadOnly
                                                bg={useColorModeValue("gray.50", "gray.700")}
                                                borderRadius="lg"
                                            />
                                        </InputGroup>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel fontWeight="medium">Reset Code</FormLabel>
                                        <HStack spacing={2} justify="center">
                                            <PinInput size="lg" value={resetCode} onChange={setResetCode} otp>
                                                <PinInputField borderRadius="md" />
                                                <PinInputField borderRadius="md" />
                                                <PinInputField borderRadius="md" />
                                                <PinInputField borderRadius="md" />
                                                <PinInputField borderRadius="md" />
                                                <PinInputField borderRadius="md" />
                                            </PinInput>
                                        </HStack>
                                        <FormHelperText color={textColor} textAlign="center">
                                            The verification code was sent to your email
                                        </FormHelperText>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel fontWeight="medium">New Password</FormLabel>
                                        <InputGroup size="md">
                                            <InputLeftElement pointerEvents="none" children={<LockIcon color="gray.400" />} />
                                            <Input
                                                type={showPassword ? "text" : "password"}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                required
                                                placeholder="Enter new password"
                                                borderRadius="lg"
                                                _focus={{
                                                    borderColor: "blue.400",
                                                    boxShadow: "0 0 0 1px blue.400",
                                                }}
                                            />
                                            <InputRightElement width="4.5rem">
                                                <Button h="1.75rem" size="sm" onClick={() => setShowPassword(!showPassword)} variant="ghost">
                                                    {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                                                </Button>
                                            </InputRightElement>
                                        </InputGroup>

                                        {newPassword && (
                                            <Box mt={2}>
                                                <Flex justify="space-between" align="center" mb={1}>
                                                    <Text fontSize="xs" color={textColor}>
                                                        Password Strength: {getPasswordStrengthText()}
                                                    </Text>
                                                    <Text fontSize="xs" fontWeight="bold" color={`${getPasswordStrengthColor()}.500`}>
                                                        {passwordStrength}%
                                                    </Text>
                                                </Flex>
                                                <Progress
                                                    value={passwordStrength}
                                                    size="sm"
                                                    colorScheme={getPasswordStrengthColor()}
                                                    borderRadius="full"
                                                />
                                            </Box>
                                        )}

                                        <FormHelperText color={textColor}>Password must be at least 8 characters</FormHelperText>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel fontWeight="medium">Confirm New Password</FormLabel>
                                        <InputGroup size="md">
                                            <InputLeftElement pointerEvents="none" children={<LockIcon color="gray.400" />} />
                                            <Input
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                required
                                                placeholder="Confirm new password"
                                                borderRadius="lg"
                                                _focus={{
                                                    borderColor: "blue.400",
                                                    boxShadow: "0 0 0 1px blue.400",
                                                }}
                                            />
                                            <InputRightElement width="4.5rem">
                                                <Button
                                                    h="1.75rem"
                                                    size="sm"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    variant="ghost"
                                                >
                                                    {showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                                                </Button>
                                            </InputRightElement>
                                        </InputGroup>
                                        {confirmPassword && newPassword !== confirmPassword && (
                                            <Text fontSize="xs" color="red.500" mt={1}>
                                                Passwords do not match
                                            </Text>
                                        )}
                                    </FormControl>

                                    <Button
                                        type="submit"
                                        colorScheme="blue"
                                        isLoading={loading}
                                        loadingText="Resetting..."
                                        size="lg"
                                        width="full"
                                        height="60px"
                                        _hover={{
                                            transform: "translateY(-2px)",
                                            boxShadow: "lg",
                                            bgGradient: "linear(to-r, blue.500, blue.600)",
                                        }}
                                        bgGradient="linear(to-r, blue.400, blue.500)"
                                        transition="all 0.3s"
                                        borderRadius="lg"
                                        fontWeight="bold"
                                        mt={4}
                                        isDisabled={newPassword !== confirmPassword}
                                    >
                                        Reset Password
                                    </Button>
                                </VStack>
                            </form>

                            {error && (
                                <Alert status="error" borderRadius="lg" variant="left-accent">
                                    <AlertIcon />
                                    <Text fontSize="sm">{error}</Text>
                                </Alert>
                            )}

                            <Button variant="link" color="blue.500" size="sm" onClick={() => navigate("/login")} fontWeight="medium">
                                Back to Login
                            </Button>
                        </VStack>
                    </Box>


                </div>
                <div className="hidden md:block md:w-1/2">
                    <img
                        src={bg || "/placeholder.svg"}
                        className="object-cover h-full w-full rounded-lg"
                        alt="Background"
                    />
                </div>
            </div>
        </div>
    )
}

export default ResetPasswordForm

